import { useCallback, useEffect, useRef, useState } from 'react';
import { request } from './client';

/**
 * New submissions, replies, inbox mail and tickets given to you, for the bell
 * and the toasts.
 *
 * Polls `/notifications` rather than `/submissions`, because listing
 * submissions writes a `submission.list_viewed` audit event — a bell checking
 * every half-minute would bury the record of real PHI access under thousands
 * of polls nobody performed.
 *
 * The feed carries no name, email or condition, only which form arrived and
 * when. That is deliberate: a toast appears over whatever the operator is
 * doing, in a room that may have other people in it. Opening the record is a
 * separate, audited act.
 */

export interface NotificationEvent {
  id: string;
  /**
   * A form that arrived, a person writing back on one (API 050), an email to
   * the enact@ inbox (051), or a ticket someone else just gave you (QA
   * C-TIC-030; only for a role with tickets:read). A ticket event reads
   * "Assigned to you" with "T-000012 · its title" as the kind, and its `form`
   * is a placeholder ('inquiry') that means nothing.
   */
  type: 'submission' | 'answer' | 'inbox' | 'ticket';
  /** The record to open; null for an inbox email or a ticket. */
  submissionId: string | null;
  /** The inbox conversation to open; null otherwise. */
  threadId: string | null;
  /** The ticket to open; null otherwise. */
  ticketId: string | null;
  form: string;
  formLabel: string;
  kind: string;
  kindLabel: string;
  status: string;
  createdAt: string;
}

/** What clicking an event opens, as the bell and the toasts end their line. */
export function opensTo(event: Pick<NotificationEvent, 'type'>): string {
  return event.type === 'inbox'
    ? 'open the conversation'
    : event.type === 'ticket'
      ? 'open the ticket'
      : 'open the record';
}

interface FeedResponse {
  serverTime: string;
  unseen: number;
  events: NotificationEvent[];
}

/** How often to ask. Slow enough to be invisible, fast enough to feel live. */
const POLL_MS = 30_000;

/** Persisted so a reload does not re-announce everything already seen. */
const SEEN_KEY = 'enact.notifications.seenAt';

const readSeen = (): string | null => {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    // Private windows and locked-down browsers throw on access rather than
    // returning null. Losing the read mark is a cosmetic problem; crashing the
    // header is not.
    return null;
  }
};

const writeSeen = (v: string): void => {
  try {
    window.localStorage.setItem(SEEN_KEY, v);
  } catch {
    /* see readSeen */
  }
};

export interface Feed {
  /** Newest first, capped by the API. */
  events: NotificationEvent[];
  /** How many arrived since the operator last opened the bell. */
  unreadCount: number;
  /** Events that arrived while this tab was open, for the toast stack. */
  toasts: NotificationEvent[];
  dismissToast: (id: string) => void;
  markAllRead: () => void;
}

export function useNotificationFeed(enabled: boolean): Feed {
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [toasts, setToasts] = useState<NotificationEvent[]>([]);
  const [seenAt, setSeenAt] = useState<string | null>(readSeen);

  /**
   * The cursor is the server's clock, not the browser's.
   *
   * Two machines never agree exactly, and a client-side timestamp would either
   * skip submissions that landed in the gap or re-announce ones it had already
   * shown. The API returns `serverTime` for exactly this.
   */
  const cursor = useRef<string | null>(null);
  const firstRun = useRef(true);

  const poll = useCallback(async () => {
    try {
      const qs = cursor.current ? `?since=${encodeURIComponent(cursor.current)}` : '';
      // Background: a bell checking on a timer is not somebody at the desk, so
      // it must not keep an unattended session from idling out. Once it has,
      // this poll's 401 is what signs the tab out (client.ts).
      const data = await request<FeedResponse>(`/notifications${qs}`, { background: true });
      cursor.current = data.serverTime;

      if (data.events.length === 0) return;

      setEvents((prev) => {
        const merged = [...data.events, ...prev];
        // The same id can arrive twice across a reconnect; keep the first.
        const seen = new Set<string>();
        return merged.filter((e) => !seen.has(e.id) && seen.add(e.id)).slice(0, 50);
      });

      // The first poll is a backfill of the last week — it populates the bell
      // but must not throw a week of toasts at someone opening the dashboard.
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }
      setToasts((prev) => [...data.events, ...prev].slice(0, 4));
    } catch {
      // A failed poll is not worth surfacing: the next one is 30 seconds away,
      // and an error banner on the header for a transient blip would be worse
      // than the blip.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void poll();
    const timer = window.setInterval(() => {
      // Skip while the tab is hidden — a backgrounded dashboard polling all
      // night is pure noise, and the next foreground poll catches up anyway
      // because the cursor has not moved.
      if (document.visibilityState === 'visible') void poll();
    }, POLL_MS);

    // Catch up immediately on return, rather than waiting out the interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, poll]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    setSeenAt(now);
    writeSeen(now);
  }, []);

  const unreadCount = seenAt
    ? events.filter((e) => e.createdAt > seenAt).length
    : events.length;

  return { events, unreadCount, toasts, dismissToast, markAllRead };
}
